'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useUAMP } from './useUAMP';
import { useChatBannerControls } from '@/components/chat-banner-provider';
import type { Notification } from '@/lib/notifications/service';

// Tier routing for critical (action-required) notifications.
//
//   visible & on matching chat   -> Tier A: ChatBannerProvider (handled here)
//   visible & elsewhere in app   -> Tier B: modal toast (handled by overlay)
//   document.hidden / unfocused  -> Tier C: rely on system push (no UI here)
//
// We dedupe by notification.id across both notification.new and
// notification.critical events because the server emits both for criticals.
//
// TIER C IS DEFERRAL, NOT A DEAD END (2026-09-07). Observed on the local
// cluster that day: an agent raised a `widget_publish_request`, the tab title
// started flashing "(!) Action required" and nothing whatsoever rendered on
// screen, at the moment of the request and still at 10s / 14s / 18s. The
// flashing title is the tell: the overlay only flashes while the queue is
// NON-empty and the tab is unfocused, so the entry had arrived, been routed
// to `system_push`, and then been stranded there permanently — nothing ever
// re-evaluated the tier when the person came back to the tab. Publishing is
// close to irreversible and the copy in that prompt is the whole safety
// mechanism, so "the person never sees it" is the worst possible failure.
//
// This is exactly the shape of request that lands unfocused: a permission
// request is raised by an app the person is actively clicking in, whereas a
// publish request is raised by an agent running somewhere else entirely.
// `promoteDeferred` below re-routes deferred entries the moment the tab is
// visible and focused again.

export type CriticalTier = 'in_chat' | 'modal_toast' | 'system_push';

export interface CriticalEntry {
  notification: Notification;
  tier: CriticalTier;
  receivedAt: number;
}

export interface UseCriticalNotificationsOptions {
  /**
   * Rows the caller has already fetched (the overlay hands over the bell's
   * first page from `useNotifications`). Anything here that
   * `isPendingCritical` accepts is adopted into the queue exactly as a live
   * WebSocket event would be, deduped by id.
   *
   * Taking the caller's existing list rather than fetching keeps this hook
   * off the network: the overlay already holds these rows, so re-surfacing a
   * pending request after a reload costs no extra request.
   */
  known?: Notification[];
}

/**
 * Does this row still need an answer, such that it may be re-raised on screen
 * after a reload? (2026-09-07)
 *
 * The second half of the same incident: the queue was built ONLY from live
 * WebSocket events, so a request raised while the tab was closed, or still
 * pending across a reload, had no path back onto the screen at all. Reloading
 * the page with the request pending showed nothing; the bell was the only
 * surface, while the agent was telling the person "Please look for the
 * approval prompt on your screen."
 *
 * `expiresAt` is the bound, and it is deliberate rather than a type list. A
 * prompt that carries a deadline is a request that is still waiting and that
 * knows when to stop asking (`widget_publish_request` sets 24h, see
 * PUBLISH_REQUEST_TTL_MS). A prompt with no deadline has no stop condition,
 * so re-raising it as a modal on every page load would nag forever.
 * `payment_required` and `widget_permission_request` set no expiry today, so
 * they keep exactly the behaviour they have now and stay bell-only after a
 * reload. Give either of them a TTL and it joins this path for free.
 *
 * `isRead` is NOT part of the test, on purpose: the MCP
 * `notifications_update({ action: 'mark_read' })` tool lets the ASKING agent
 * mark its own prompt read without deciding it, so keying on `isRead` would
 * let an agent hide the prompt it raised. Only `actionTaken` (the person's
 * decision) closes a request. Same rule as the pending-request dedup in
 * lib/widgets/publish-approval.ts.
 */
export function isPendingCritical(n: Notification | null | undefined): boolean {
  if (!n) return false;
  if (n.urgency !== 'critical') return false;
  if (n.actionTaken) return false;
  if (!n.expiresAt) return false;
  // Over the wire this is an ISO string; from a direct DB read it is a Date.
  const expiresAt = new Date(n.expiresAt as unknown as string | Date).getTime();
  if (!Number.isFinite(expiresAt)) return false;
  return expiresAt > Date.now();
}

function extractChatId(notification: Notification): string | undefined {
  const data = notification.data as Record<string, unknown> | null;
  const chatId = data?.chatId;
  return typeof chatId === 'string' && chatId.length > 0 ? chatId : undefined;
}

function pathnameMatchesChat(pathname: string, chatId: string): boolean {
  // Routes that show a specific chat. Be permissive — any path that ends
  // with /<chatId> or /c/<chatId> counts as "viewing this chat".
  if (!pathname) return false;
  const segments = pathname.split('/').filter(Boolean);
  return segments.includes(chatId);
}

function isVisible(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function useCriticalNotifications(
  options: UseCriticalNotificationsOptions = {},
) {
  const { known } = options;
  const { on } = useUAMP();
  const pathname = usePathname() || '';
  const banners = useChatBannerControls();
  const [queue, setQueue] = useState<CriticalEntry[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Ids the person has already closed out in this session (dismissed,
  // actioned, or marked read). Kept SEPARATE from `seenIdsRef`, which the
  // mark-all-read event clears: without this, the next adoption pass over
  // `known` would raise the toast again for something already answered.
  const dismissedIdsRef = useRef<Set<string>>(new Set());

  const removeFromQueue = useCallback((notificationId: string) => {
    dismissedIdsRef.current.add(notificationId);
    setQueue((prev) => prev.filter((e) => e.notification.id !== notificationId));
    banners.dismissNotification(notificationId);
  }, [banners]);

  // A chat is on screen when the route names it OR a chat window shows it
  // (components/chat-banner-provider.tsx says why the second is needed).
  const { isChatOpen } = banners;
  const chatOnScreen = useCallback(
    (chatId: string) => pathnameMatchesChat(pathname, chatId) || isChatOpen(chatId),
    [pathname, isChatOpen],
  );

  const route = useCallback((notification: Notification): CriticalTier => {
    const visible = isVisible();
    if (!visible) return 'system_push';
    const chatId = extractChatId(notification);
    if (chatId && chatOnScreen(chatId)) return 'in_chat';
    return 'modal_toast';
  }, [chatOnScreen]);

  const handle = useCallback((notification: Notification) => {
    if (!notification || notification.urgency !== 'critical') return;
    if (seenIdsRef.current.has(notification.id)) return;
    if (dismissedIdsRef.current.has(notification.id)) return;
    seenIdsRef.current.add(notification.id);

    // If the user already acted (race: SW handled it before client got the
    // event), drop it on the floor.
    if (notification.actionTaken) return;

    const tier = route(notification);
    const entry: CriticalEntry = {
      notification,
      tier,
      receivedAt: Date.now(),
    };

    if (tier === 'in_chat') {
      const chatId = extractChatId(notification);
      if (chatId) {
        banners.pushBanner(chatId, { notification });
      }
      // Also keep in queue so we can re-route if the user navigates away.
    }

    setQueue((prev) => [...prev, entry]);
  }, [route, banners]);

  // `handle` changes identity on every navigation (it closes over `route`,
  // which closes over `pathname`). The adoption effect below must not re-run
  // for that reason alone, so it reaches the current one through a ref.
  const handleRef = useRef(handle);
  handleRef.current = handle;

  useEffect(() => {
    const offNew = on('notification.new', ({ notification }) => {
      handle(notification as unknown as Notification);
    });
    const offCritical = on('notification.critical', ({ notification }) => {
      handle(notification as unknown as Notification);
    });
    const offRead = on('notification.read', ({ all, notificationId }) => {
      if (all) {
        setQueue((prev) => {
          // "Mark all read" is an explicit "I have seen these" — remember the
          // ids so a later adoption pass does not raise them again. Adding to
          // a Set is idempotent, so a StrictMode double-invoke is harmless.
          for (const e of prev) dismissedIdsRef.current.add(e.notification.id);
          return [];
        });
        seenIdsRef.current.clear();
        return;
      }
      if (notificationId) removeFromQueue(notificationId);
    });
    return () => {
      offNew?.();
      offCritical?.();
      offRead?.();
    };
  }, [on, handle, removeFromQueue]);

  // Adopt already-known pending requests (see `isPendingCritical`). This is
  // what puts a publish approval back on screen after a reload; `handle`
  // dedupes by id, so a row that also arrives live is only queued once.
  useEffect(() => {
    if (!known || known.length === 0) return;
    for (const n of known) {
      if (isPendingCritical(n)) handleRef.current(n);
    }
  }, [known]);

  // Re-route entries when the matching chat comes on or goes off screen (a
  // navigation, or a chat window opening or closing). A modal-toast entry
  // collapses into the chat's banner; an in-chat entry whose chat is no
  // longer shown goes back to the toast (2026-11-03: with windows, "shown"
  // can end without a navigation, and a banner in an unmounted chat view is
  // a prompt nobody can see).
  const { pushBanner, dismissNotification: dropBanner, openChatsVersion } = banners;
  useEffect(() => {
    setQueue((prev) => {
      let mutated = false;
      const next = prev.map((entry) => {
        const chatId = extractChatId(entry.notification);
        if (!chatId) return entry;
        if (entry.tier === 'modal_toast' && chatOnScreen(chatId)) {
          pushBanner(chatId, { notification: entry.notification });
          mutated = true;
          return { ...entry, tier: 'in_chat' as CriticalTier };
        }
        if (entry.tier === 'in_chat' && !chatOnScreen(chatId)) {
          dropBanner(entry.notification.id);
          mutated = true;
          return { ...entry, tier: 'modal_toast' as CriticalTier };
        }
        return entry;
      });
      return mutated ? next : prev;
    });
  }, [chatOnScreen, openChatsVersion, pushBanner, dropBanner]);

  // Promote deferred (Tier C) entries once the tab is visible AND focused
  // again. Without this the tier decided at arrival time was final, and a
  // request that landed on an unfocused tab could never reach the screen —
  // the 2026-09-07 publish-approval defect described at the top of this file.
  // Tier C semantics are preserved: nothing chimes or renders while the tab
  // is away, the system push is still the alert for that window.
  const promoteDeferred = useCallback(() => {
    if (!isVisible()) return;
    setQueue((prev) => {
      let mutated = false;
      const next = prev.map((entry) => {
        if (entry.tier !== 'system_push') return entry;
        const tier = route(entry.notification);
        if (tier === entry.tier) return entry;
        mutated = true;
        if (tier === 'in_chat') {
          const chatId = extractChatId(entry.notification);
          if (chatId) {
            banners.pushBanner(chatId, { notification: entry.notification });
          }
        }
        return { ...entry, tier };
      });
      return mutated ? next : prev;
    });
  }, [route, banners]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Run once on mount too: a row adopted from `known` during a load that
    // began in a background tab is already sitting in Tier C by the time the
    // listeners attach, and `focus` will not fire again if the tab was
    // foregrounded before hydration finished.
    promoteDeferred();
    const onWake = () => promoteDeferred();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
    };
  }, [promoteDeferred]);

  return {
    queue,
    dismiss: removeFromQueue,
  };
}
